/* eslint-disable @typescript-eslint/no-explicit-any */
import type { PageSideNavDivider, PageSideNavLink } from "@octopusdeploy/design-system-components";
import { Callout, Level2OuterPageLayout, useIsLargerThanIpadResolution, useSetInPageNavVisible, useShowSnackbar } from "@octopusdeploy/design-system-components";
import { CodeBranchIcon } from "@octopusdeploy/design-system-icons";
import { themeTokens } from "@octopusdeploy/design-system-tokens";
import { useMutation, useQuery, APIOperationStatusProvider } from "@octopusdeploy/octopus-react-client";
import type { BranchSpecifier, ProjectTemplateResource, ValidateGitRefV2Response, Repository, PublishProjectTemplateCommand, ShareProjectTemplateToManySpacesCommand } from "@octopusdeploy/octopus-server-client";
import { UseDefaultBranch, ValidateGitRefV2ResponseType } from "@octopusdeploy/octopus-server-client";
import { links } from "@octopusdeploy/portal-routes";
import { compact } from "lodash";
import type { ReactNode } from "react";
import * as React from "react";
import { useState, useReducer, useMemo, useRef } from "react";
import { ShareTemplateSpacesType } from "~/areas/platformHub/processTemplates/ShareTemplateDialog";
import { createSnackbarMessage } from "~/areas/platformHub/processTemplates/TemplateParametersTable";
import type { WizardSaveParams, PublishWizardVersion } from "~/areas/platformHub/projectTemplates/ProjectTemplateWizardDialog";
import { ActionCompleted, ProjectTemplateWizardDialog } from "~/areas/platformHub/projectTemplates/ProjectTemplateWizardDialog";
import { repository } from "~/clientInstance";
import type { DataBaseComponentState } from "~/components/DataBaseComponent/DataBaseComponent";
import { DataBaseComponent } from "~/components/DataBaseComponent/DataBaseComponent";
import { useDialogTrigger } from "~/components/Dialog/DialogTrigger";
import ErrorContextProvider from "~/components/ErrorContext/ErrorContext";
import ErrorPanel from "~/components/ErrorPanel/ErrorPanel";
import type { PageContentCalloutType } from "~/components/PageContent/PageContent";
import { SkeletonLoadingLayout } from "~/components/SkeletonLoadingLayout/SkeletonLoadingLayout";
import { useCreatePageSideNavLink } from "~/hooks/useCreatePageSideNavLink";
import type { CreatePageSideNavLink } from "~/hooks/useCreatePageSideNavLink";
import { ProjectTemplateStatusBar, projectTemplateStatusReducer, initialProjectTemplateStatusState } from "./ProjectTemplateStatus";
import type { ProjectTemplateStatus } from "./ProjectTemplateStatus";
import { ProjectTemplateStatusContextProvider } from "./ProjectTemplateStatusContext";
import { ProjectTemplateWizardDialogContextProvider } from "./ProjectTemplateWizardDialogContext";
export interface ProjectTemplateLayoutProps {
    slug: string;
    branchName: string;
    isNewlyCreatedProject?: string | null;
    configureProject?: boolean;
    defaultActionType?: string | null;
    scrollAreaRef: React.RefObject<HTMLDivElement>;
    projectStatus: any; // TODO: Import proper type
    children: (params: {
        projectTemplateContext: any; // TODO: Define proper context type
        projectStatus: any;
        projectTemplateStatus: ProjectTemplateStatus;
        wizardDialog: ProjectTemplateWizardDialogContext;
    }) => React.ReactNode;
}
export interface ProjectTemplateWizardDialogContext {
    openWizard: (steps: number[], onAfterSave?: () => void, commitHandler?: (commitMessage: string) => Promise<void>) => void;
    saveProjectTemplate: (params: WizardSaveParams) => Promise<void>;
    sharingConfiguration: {
        SharedToAllSpaces: boolean;
        IndividuallySharedSpaceIds: string[];
    } | null;
    reloadSharingConfiguration: () => void;
    publishedVersions: PublishWizardVersion[];
    reloadPublishedVersions: () => void;
    refetchProjectTemplateStatus: () => void;
    showInitialSuccessCallout: boolean;
    setShowInitialSuccessCallout: (show: boolean) => void;
}
export function ProjectTemplateLayout({ slug, branchName, isNewlyCreatedProject, configureProject, defaultActionType, scrollAreaRef, projectStatus, children }: ProjectTemplateLayoutProps) {
    const createPageSideNavLink = useCreatePageSideNavLink();
    const [projectTemplateStatusState, projectTemplateStatusDispatch] = useReducer(projectTemplateStatusReducer, initialProjectTemplateStatusState);
    const refetchProjectTemplateStatus = useRef<(() => void) | undefined>(undefined);
    const handleRefetchReady = React.useCallback((refetch: () => void) => {
        refetchProjectTemplateStatus.current = refetch;
    }, []);
    return (<ErrorContextProvider>
            <ProjectTemplateLayoutInternal slug={slug} branchName={branchName} isNewlyCreatedProject={isNewlyCreatedProject} configureProject={configureProject} defaultActionType={defaultActionType} scrollAreaRef={scrollAreaRef} projectStatus={projectStatus} projectTemplateStatus={{
            state: projectTemplateStatusState,
            dispatch: projectTemplateStatusDispatch,
            refetch: refetchProjectTemplateStatus.current,
        }} onRefetchReady={handleRefetchReady} createPageSideNavLink={createPageSideNavLink}>
                {children}
            </ProjectTemplateLayoutInternal>
        </ErrorContextProvider>);
}
type ProjectTemplateLayoutInternalProps = {
    slug: string;
    branchName: string;
    isNewlyCreatedProject: string | null | undefined;
    configureProject: boolean | null | undefined;
    defaultActionType?: string | null | undefined;
    scrollAreaRef: React.RefObject<HTMLDivElement>;
    projectStatus: any;
    projectTemplateStatus: ProjectTemplateStatus;
    onRefetchReady: (refetch: () => void) => void;
    createPageSideNavLink: CreatePageSideNavLink;
    children: (params: {
        projectTemplateContext: any;
        projectStatus: any;
        projectTemplateStatus: ProjectTemplateStatus;
        wizardDialog: ProjectTemplateWizardDialogContext;
    }) => ReactNode;
};
interface ProjectTemplateLayoutState extends DataBaseComponentState {
    projectTemplate: ProjectTemplateResource | null;
    gitRefValidationError: ValidateGitRefV2Response | null;
}
function ProjectTemplateWizardDialogProvider({ projectTemplate, gitRef, projectTemplateStatus, onRefetchReady, children, }: {
    projectTemplate: ProjectTemplateResource;
    gitRef: string;
    projectTemplateStatus: ProjectTemplateStatus;
    onRefetchReady: (refetch: () => void) => void;
    children: (wizardDialog: ProjectTemplateWizardDialogContext) => ReactNode;
}) {
    const correlationContext = useMemo(() => ({ operation: "ProjectTemplateWizardDialog" }), []);
    return (<APIOperationStatusProvider correlationContext={correlationContext}>
            <ProjectTemplateWizardDialogProviderInner projectTemplate={projectTemplate} gitRef={gitRef} projectTemplateStatus={projectTemplateStatus} onRefetchReady={onRefetchReady}>
                {children}
            </ProjectTemplateWizardDialogProviderInner>
        </APIOperationStatusProvider>);
}
function ProjectTemplateWizardDialogProviderInner({ projectTemplate, gitRef, projectTemplateStatus, onRefetchReady, children, }: {
    projectTemplate: ProjectTemplateResource;
    gitRef: string;
    projectTemplateStatus: ProjectTemplateStatus;
    onRefetchReady: (refetch: () => void) => void;
    children: (wizardDialog: ProjectTemplateWizardDialogContext) => ReactNode;
}) {
    const showSnackbar = useShowSnackbar();
    const { openDialog: openWizardDialog, isOpen: isWizardDialogOpen, closeAllDialogs: closeWizardDialog } = useDialogTrigger();
    const [wizardSteps, setWizardSteps] = useState<number[]>([]);
    const [onAfterSaveCallback, setOnAfterSaveCallback] = useState<(() => void) | undefined>(undefined);
    const [commitHandler, setCommitHandler] = useState<((commitMessage: string) => Promise<void>) | undefined>(undefined);
    const [showInitialSuccessCallout, setShowInitialSuccessCallout] = useState<boolean>(false);
    const { result: spaces } = useQuery((repo: Repository) => repo.Spaces.all(), [], "Get spaces for project template share");
    const { result: publishedVersions, refetch: reloadPublishedVersions } = useQuery((repo: Repository) => repo.ProjectTemplates.getVersions(projectTemplate.Slug).then((r) => r.ProjectTemplateVersions.map((v) => ({ Version: v.Version, IsPreRelease: v.IsPreRelease }))), [projectTemplate.Slug], "Get project template versions");
    const { result: sharingConfiguration, refetch: reloadSharingConfiguration } = useQuery((repo: Repository) => repo.ProjectTemplates.getSharedSpaces(projectTemplate.Slug), [projectTemplate.Slug], "Get shared spaces for project template");
    const reloadDataAfterSave = () => {
        reloadPublishedVersions();
        reloadSharingConfiguration();
        if (projectTemplateStatus.refetch) {
            projectTemplateStatus.refetch();
        }
    };
    const { execute: saveProjectTemplate } = useMutation({
        name: "Save project template from wizard",
        action: async (repository: Repository, params: WizardSaveParams) => {
            if (params.type === "Wizard") {
                const { commitParams, publishParams, shareParams } = params;
                const actionsCompleted: ActionCompleted[] = [];
                if (commitParams && commitHandler) {
                    const commitMessage = commitParams.commitMessage?.summary || "Update project template";
                    await commitHandler(commitMessage);
                    actionsCompleted.push(ActionCompleted.Committed);
                }
                if (publishParams) {
                    const { versionToPublish, releaseNotes } = publishParams;
                    const publishCommand: PublishProjectTemplateCommand = {
                        ProjectTemplateSlug: projectTemplate.Slug,
                        GitRef: projectTemplate.GitRef,
                        Version: versionToPublish,
                        ReleaseNotes: releaseNotes,
                    };
                    await repository.ProjectTemplates.publishVersion(publishCommand);
                    actionsCompleted.push(ActionCompleted.Published);
                }
                if (shareParams) {
                    const { shareType, selectedSpaces } = shareParams;
                    const shareCommand: ShareProjectTemplateToManySpacesCommand = {
                        ProjectTemplateSlug: projectTemplate.Slug,
                        GitRef: projectTemplate.GitRef,
                        ShareToAllSpaces: shareType === ShareTemplateSpacesType.ShareAll,
                        IndividuallySharedSpaceIds: shareType === ShareTemplateSpacesType.ShareIndividual ? selectedSpaces : [],
                    };
                    await repository.ProjectTemplates.shareToSpace(shareCommand);
                    actionsCompleted.push(ActionCompleted.Shared);
                }
                return actionsCompleted;
            }
            return [];
        },
        afterComplete: async (repository, result) => {
            const snackbarMessage = createSnackbarMessage(result, "project template");
            if (snackbarMessage) {
                showSnackbar(snackbarMessage);
            }
            reloadDataAfterSave();
            closeWizardDialog();
            if (onAfterSaveCallback) {
                onAfterSaveCallback();
            }
        },
    });
    const openWizard = (steps: number[], onAfterSave?: () => void, handler?: (commitMessage: string) => Promise<void>) => {
        setWizardSteps(steps);
        if (onAfterSave) {
            setOnAfterSaveCallback(() => onAfterSave);
        }
        if (handler) {
            setCommitHandler(() => handler);
        }
        openWizardDialog();
    };
    const wizardDialogContext: ProjectTemplateWizardDialogContext = {
        openWizard,
        saveProjectTemplate,
        sharingConfiguration: sharingConfiguration ?? null,
        reloadSharingConfiguration,
        publishedVersions: publishedVersions ?? [],
        reloadPublishedVersions,
        refetchProjectTemplateStatus: () => projectTemplateStatus.refetch?.(),
        showInitialSuccessCallout,
        setShowInitialSuccessCallout,
    };
    return (<>
            {children(wizardDialogContext)}

            {publishedVersions && spaces ? (<ProjectTemplateWizardDialog open={isWizardDialogOpen} busy={false} errors={[]} model={projectTemplate} gitRef={gitRef} spaces={spaces} sharingConfiguration={sharingConfiguration ?? null} publishedVersions={publishedVersions} stepsToShow={wizardSteps} getDefaultCommitMessage={() => "Update project template"} saveProjectTemplate={saveProjectTemplate} closeDialog={closeWizardDialog} key={isWizardDialogOpen ? "dialog-open" : "dialog-closed"} setShowInitialSuccessCallout={setShowInitialSuccessCallout}/>) : null}
        </>);
}
class ProjectTemplateLayoutInternal extends DataBaseComponent<ProjectTemplateLayoutInternalProps, ProjectTemplateLayoutState> {
    constructor(props: ProjectTemplateLayoutInternalProps) {
        super(props);
        this.state = {
            projectTemplate: null,
            gitRefValidationError: null,
        };
    }
    async componentDidMount() {
        await this.doBusyTask(async () => {
            const projectTemplate = await repository.ProjectTemplates.get(this.props.slug, this.props.branchName);
            this.setState({
                projectTemplate,
                gitRefValidationError: null,
            });
        });
    }
    toBranchSpecifier(branch: string | undefined): BranchSpecifier {
        return branch ? branch : UseDefaultBranch;
    }
    render() {
        const branchSpecifier = this.toBranchSpecifier(this.props.branchName);
        if (!this.state.projectTemplate) {
            return (<main>
                    <HideAreaNavPanelWorkaround />
                    <SkeletonLoadingLayout errors={this.errors}/>
                </main>);
        }
        // Create mock project template context
        const projectTemplateContext = {
            state: {
                model: this.state.projectTemplate,
                gitRefValidationError: this.state.gitRefValidationError,
            },
        };
        const navigationSidebarContent = this.state.gitRefValidationError?.Type === ValidateGitRefV2ResponseType.ConnectionFailed ? (this.getGitErrorPanel(this.state.gitRefValidationError)) : (<ProjectTemplateWizardDialogProvider projectTemplate={this.state.projectTemplate} gitRef={this.props.branchName} projectTemplateStatus={this.props.projectTemplateStatus} onRefetchReady={this.props.onRefetchReady}>
                    {(wizardDialog) => (<ProjectTemplateWizardDialogContextProvider value={wizardDialog}>
                            {this.props.children({
                    projectTemplateContext,
                    projectStatus: this.props.projectStatus,
                    projectTemplateStatus: this.props.projectTemplateStatus,
                    wizardDialog,
                })}
                        </ProjectTemplateWizardDialogContextProvider>)}
                </ProjectTemplateWizardDialogProvider>);
        const contentWithStatusContext = <ProjectTemplateStatusContextProvider value={this.props.projectTemplateStatus}>{navigationSidebarContent}</ProjectTemplateStatusContextProvider>;
        return (<main>
                {this.renderErrors()}
                <Level2OuterPageLayout navItems={this.getProjectTemplateNavItems(this.state.projectTemplate)} header={{
                breadcrumbs: this.headerBreadcrumbs(),
                title: this.state.projectTemplate.Name,
                logo: this.getProjectTemplateLogo(this.state.projectTemplate),
                titleIcon: <ProjectTemplatePageHeaderTitleIcon />,
                statusSection: this.props.projectTemplateStatus ? (<ProjectTemplateStatusBar slug={this.props.slug} gitRef={this.props.branchName} state={this.props.projectTemplateStatus.state} dispatch={this.props.projectTemplateStatus.dispatch} enableLinks onRefetchReady={this.props.onRefetchReady}/>) : undefined,
            }}>
                    {contentWithStatusContext}
                </Level2OuterPageLayout>
            </main>);
    }
    private getProjectTemplateNavItems(projectTemplate: ProjectTemplateResource) {
        const navigationLinks: (PageSideNavLink | PageSideNavDivider | null)[] = [];
        navigationLinks.push(this.props.createPageSideNavLink("Process", links.projectTemplateDeploymentProcessPage.generateUrl({ slug: projectTemplate.Slug, gitRef: this.props.branchName }), true));
        navigationLinks.push("divider");
        navigationLinks.push(this.props.createPageSideNavLink("Variables", links.projectTemplateVariablesPage.generateUrl({ slug: projectTemplate.Slug, gitRef: this.props.branchName }), true));
        navigationLinks.push(this.props.createPageSideNavLink("Parameters", links.projectTemplateBranchParametersPage.generateUrl({ slug: projectTemplate.Slug, gitRef: this.props.branchName }), true));
        navigationLinks.push("divider");
        navigationLinks.push(this.props.createPageSideNavLink("Versions", links.projectTemplateVersionsPage.generateUrl({ slug: projectTemplate.Slug, gitRef: this.props.branchName }), true));
        navigationLinks.push("divider");
        return compact(navigationLinks);
    }
    private getGitErrorPanel(error: ValidateGitRefV2Response) {
        return (<div>
                <h3>Git Reference Error</h3>
                <p>There was an issue with the git reference {error.Message}</p>
            </div>);
    }
    renderErrors() {
        const errors = this.state && this.errors;
        if (!errors) {
            return null;
        }
        return <ErrorPanel message={errors.message} errors={errors.errors} parsedHelpLinks={errors.parsedHelpLinks} helpText={errors.helpText} helpLink={errors.helpLink}/>;
    }
    private headerBreadcrumbs() {
        return [{ label: "Project Templates", pageUrl: links.projectTemplatesPage.generateUrl() }];
    }
    private getProjectTemplateLogo(projectTemplate: ProjectTemplateResource) {
        const iconUrl = repository.Icons.getIconUrl(projectTemplate.Icon);
        return iconUrl ? { href: iconUrl, accessibleName: `${projectTemplate.Name} icon` } : undefined;
    }
    static displayName = "ProjectTemplateLayoutInternal";
}
function ProjectTemplatePageHeaderTitleIcon() {
    const isLargerThanIpad = useIsLargerThanIpadResolution();
    return <>{isLargerThanIpad ? <CodeBranchIcon size={20} color={themeTokens.color.icon.tertiary}/> : undefined}</>;
}
/*
Currently the routes we have for branch vs non branch based pages co-exist in the same menu.
This means that the `ProjectTemplateLayout` currently gets unmounted and remounted between these
routes. This is especially noticeable for the vertical navigation as we show the area nav
panel when the in page menu is not visible. This is exactly what happens when we navigate
between a git based page vs a non git based page when vertical navigation is enabled.
This workaround provides us with a cheap way to make this transition less jarring until
we can find a way to properly refactor the layout to either be mounted higher up in the
tree OR we consolidate routes in some way (possibly using URL query parameters instead).
 */
function HideAreaNavPanelWorkaround() {
    const setVisible = useSetInPageNavVisible();
    React.useEffect(() => {
        setVisible?.(true);
        return () => setVisible?.(false);
    }, [setVisible]);
    return null;
}
interface GetProjectTemplateInitialSuccessCalloutArgs {
    showInitialSuccessCallout: boolean;
    setShowInitialSuccessCallout: (value: boolean) => void;
    projectTemplateName: string;
}
export function GetProjectTemplateInitialSuccessCallout({ showInitialSuccessCallout, setShowInitialSuccessCallout, projectTemplateName }: GetProjectTemplateInitialSuccessCalloutArgs): PageContentCalloutType | undefined {
    if (showInitialSuccessCallout) {
        return {
            type: "custom",
            content: (<Callout type={"success"} title={`"${projectTemplateName}" successfully committed, published and shared.`} canClose={true} onClose={() => setShowInitialSuccessCallout(false)}>
                    This template can now be used when creating a project.
                </Callout>),
        };
    }
    return undefined;
}
